import React from 'react';
import {
  Dialog,
  IconButton,
  MenuList,
  MenuItem,
  ListItemText,
  Link,
  Toolbar,
} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import CloseIcon from '@material-ui/icons/Close';
import {
  Theme,
  createStyles,
  makeStyles,
  styled,
  useTheme,
} from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';

import { StyledButton } from '@bizapp-lp/workflow/molecules/StyledButton';

export interface HeaderLinkProps {
  url: string;
  label: string;
}

export interface HeaderContentProps {
  links?: HeaderLinkProps[];
  label?: string;
  onClick?: () => void;
}

export const HeaderContent: React.FC<HeaderContentProps> = ({
  links,
  label,
  onClick,
}) => {
  const HeaderNav = styled('nav')(({ theme }) => ({
    marginLeft: 'auto',
    '& > *': {
      marginLeft: theme.spacing(3),
    },
  }));

  const classes = makeStyles((theme: Theme) =>
    createStyles({
      button: {
        width: '170px',
        height: '32px',
      },
      links: {
        color: '#636363',
        fontSize: '14px',
        letterSpacing: 0,
        display: 'none',
        [theme.breakpoints.up('sm')]: {
          display: 'inline-flex',
          alignItems: 'center',
        },
        '&:link': { color: '#333333' },
        '&:visited': { color: '#333333' },
        '&:action': {
          color: '#333333',
          textDecoration: 'underline',
        },
        '&:focus': {
          color: '#333333',
          textDecoration: 'underline',
        },
        '&:hover': {
          color: '#333333',
          textDecoration: 'underline',
        },
      },
    }),
  )();

  return (
    <>
      <HeaderNav>
        {links &&
          links.map((link, i) => (
            <Link
              className={classes.links}
              component={RouterLink}
              to={link.url}
              key={`header-link-${i}`}
            >
              {link.label}
            </Link>
          ))}
        <StyledButton
          shallow
          label={label}
          onClick={onClick}
          className={classes.button}
        />
      </HeaderNav>
    </>
  );
};

export const HeaderContentDrawer: React.FC<HeaderContentProps> = ({
  links,
  label,
  onClick,
}) => {
  const theme = useTheme();

  const [open, setOpen] = React.useState(false);
  const handleToggle = () => {
    setOpen((prev) => !prev);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const classes = makeStyles((theme: Theme) =>
    createStyles({
      menuButton: {
        margin: theme.spacing(0, -1.5, 0, 'auto'),
        color: '#7f7f7f',
      },
      dialog: {
        '& .MuiPaper-root': {
          position: 'absolute',
          top: 0,
          margin: 0,
          width: '100%',
          maxWidth: '100%',
          borderRadius: 0,
        },
        '& .MuiDialog-paperScrollPaper': {
          maxHeight: 'calc(100% - 32px)',
        },
      },
      bgToolbar: { height: theme.spacing(9) },
      headerFirstMenu: {
        marginTop: theme.spacing(2),
      },
      dialogButton: {
        width: 'calc(100% - 32px)',
        maxWidth: '343px',
        height: '32px',
        minHeight: '32px',
        margin: theme.spacing(1.5, 'auto', 2.5),
      },
      menuList: {
        padding: theme.spacing(2, 0, 0),
      },
    }),
  )();

  return (
    <>
      <IconButton
        aria-label="open menu"
        edge="end"
        onClick={handleToggle}
        className={classes.menuButton}
      >
        {open ? <CloseIcon /> : <MenuIcon />}
      </IconButton>
      <Dialog
        open={open}
        onClose={handleClose}
        style={{ zIndex: theme.zIndex.appBar - 1 }}
        className={classes.dialog}
      >
        <>
          <Toolbar className={classes.bgToolbar} />
          {links && (
            <MenuList className={classes.menuList}>
              {links.map((link, i) => (
                <MenuItem
                  button
                  component={RouterLink}
                  to={link.url}
                  key={`${i}`}
                >
                  <ListItemText>{link.label}</ListItemText>
                </MenuItem>
              ))}
            </MenuList>
          )}
          <StyledButton
            shallow
            label={label}
            className={classes.dialogButton}
            onClick={onClick}
          />
        </>
      </Dialog>
    </>
  );
};
